import { useState } from "react";
import "@/App.css";
import axios from "axios";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const IMAGES = {
  cap1: "https://images.unsplash.com/photo-1507692744406-b171ab8feabd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMzN8MHwxfHNlYXJjaHwxfHxhbWJlciUyMHNvdW5kJTIwd2F2ZSUyMGFic3RyYWN0fGVufDB8fHx8MTc5MjA1ODg2NXww&ixlib=rb-4.1.0&q=85",
  cap2: "https://images.unsplash.com/photo-1760780567530-389d8a3fba75?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwzfHxhY291c3RpYyUyMHN0dWRpbyUyMHBhbmVsJTIwYXJjaGl0ZWN0dXJlfGVufDB8fHx8MTc5MjA1ODg2NXww&ixlib=rb-4.1.0&q=85",
  cap3: "https://images.unsplash.com/photo-1625737187400-518257d2dcf3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwyfHxicm9uemUlMjB0ZXh0dXJlJTIwYWJzdHJhY3R8ZW58MHx8fHwxNzkyMDU4ODY1fDA&ixlib=rb-4.1.0&q=85",
  strip: "https://images.unsplash.com/photo-1738666831048-fc9e53345692?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwzfHxhdWRpdG9yaXVtJTIwYWNvdXN0aWMlMjBwYW5lbHMlMjBhbWJlcnxlbnwwfHx8fDE3OTIwNTg4NjV8MA&ixlib=rb-4.1.0&q=85",
};

const MARQUEE_ITEMS = [
  "Zero Latency Voice Turn-Taking",
  "Acoustic Micro-Cadence Synthesis",
  "Global SIP Telephony Interconnect",
  "Enterprise Privacy Boundaries",
];

const CAPABILITIES = [
  {
    image: IMAGES.cap1,
    alt: "Spectral sound wave visual in amber tones",
    title: "Natural Phoneme Flow",
    text: "Eliminates robotic artifacts and synthetic pause friction across long calls.",
    testid: "capability-card-phoneme-flow",
  },
  {
    image: IMAGES.cap2,
    alt: "Acoustic studio panel architecture",
    title: "Dynamic Interruption",
    text: "Listens and yields instantaneously when callers speak over automated responses.",
    testid: "capability-card-dynamic-interruption",
  },
  {
    image: IMAGES.cap3,
    alt: "Network nodes over textured bronze plate",
    title: "Dedicated Edge Clusters",
    text: "Isolated telephony nodes built to meet enterprise data governance mandates.",
    testid: "capability-card-edge-clusters",
  },
];

const Nav = () => (
  <nav data-testid="site-nav" className="sticky top-0 z-50 border-b border-[var(--cf-border-15)] bg-[var(--cf-bg)]/90 backdrop-blur-md">
    <div className="max-w-6xl mx-auto px-6 h-20 flex items-center justify-between">
      <a href="#top" data-testid="nav-brand-link" className="flex items-center gap-3">
        <svg className="w-6 h-6 text-[var(--cf-accent)]" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
          <circle cx="12" cy="12" r="9"></circle>
          <path d="M12 3v18"></path>
          <path d="M3 12h18"></path>
        </svg>
        <span className="cf-serif-title text-xl tracking-wider text-white">Crestfoundry</span>
      </a>

      <a data-testid="nav-get-access-button" href="#contact" className="px-5 py-2 text-xs uppercase tracking-widest border border-[var(--cf-border-40)] text-[var(--cf-accent)] hover:bg-[var(--cf-accent)]/10 rounded-full transition-colors">
        Get Access
      </a>
    </div>
  </nav>
);

const Hero = () => (
  <section id="top" data-testid="hero-section" className="py-20 md:py-28 border-b border-[var(--cf-border-10)]">
    <div className="max-w-6xl mx-auto px-6">
      <div className="grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-7">
          <span data-testid="hero-eyebrow" className="cf-mono text-xs uppercase tracking-widest text-[var(--cf-accent)] block mb-4">Enterprise Voice AI &mdash; Beta Access Open</span>
          <h1 data-testid="hero-headline" className="cf-serif-title text-4xl sm:text-6xl lg:text-7xl tracking-tight leading-[1.05] text-[var(--cf-heading)] mb-8">
            <span className="cf-ln"><span>Voice Artificial Intelligence</span></span>
            <span className="cf-ln"><span className="italic font-light text-[var(--cf-accent)]">Refined For Telephony.</span></span>
          </h1>
          <p data-testid="hero-subline" className="text-lg text-neutral-300 max-w-xl leading-relaxed mb-8">
            Enterprise-ready from day one: natural vocal cadence, dedicated infrastructure, and governance-grade privacy for customer calls. Join the waitlist for full access to the enterprise product.
          </p>
          <div className="flex items-center gap-4">
            <a data-testid="hero-cta-button" href="#contact" className="px-8 py-3.5 bg-[var(--cf-accent)] text-[var(--cf-bg)] font-medium text-sm tracking-wide rounded-full hover:bg-[var(--cf-accent-hover)] transition-colors">
              Join the Enterprise Waitlist
            </a>
          </div>
        </div>
        <div className="lg:col-span-5 cf-photo-spot">
          <div data-testid="hero-product-console" className="relative rounded-2xl overflow-hidden border border-[var(--cf-border-20)] aspect-[4/5] shadow-2xl bg-[var(--cf-card)]">
            <div className="p-6 md:p-7 flex flex-col h-full">
              <div className="flex items-center gap-2 mb-6">
                <span className="w-2.5 h-2.5 rounded-full bg-[#3f3f46]"></span>
                <span className="w-2.5 h-2.5 rounded-full bg-[#3f3f46]"></span>
                <span className="w-2.5 h-2.5 rounded-full bg-[var(--cf-accent)]"></span>
                <span className="cf-mono text-[11px] tracking-widest text-neutral-400 ml-2 uppercase">Crestfoundry Console</span>
              </div>
              <div className="border border-[var(--cf-border-15)] rounded-xl p-5 bg-black/40 mb-4">
                <div className="flex items-center gap-2 mb-4">
                  <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse"></span>
                  <span className="cf-mono text-[11px] tracking-widest text-emerald-300 uppercase">Live Call &mdash; SIP Trunk</span>
                </div>
                <div className="flex items-end gap-1 h-14 mb-4" aria-hidden="true">
                  {[0.5, 0.9, 0.35, 0.75, 1, 0.55, 0.85, 0.4, 0.95, 0.6, 0.8, 0.45, 0.7, 1, 0.5, 0.9, 0.38, 0.78, 0.62, 0.88].map((h, i) => (
                    <span key={i} className="cf-eq-bar flex-1 rounded-sm" style={{ height: `${h * 100}%`, animationDelay: `${i * 0.09}s`, backgroundColor: 'rgba(226, 168, 92, 0.75)' }}></span>
                  ))}
                </div>
                <div className="space-y-2.5">
                  <div className="flex justify-start"><span className="text-[11px] cf-mono text-neutral-300 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5">Caller &mdash; speaking</span></div>
                  <div className="flex justify-end"><span className="text-[11px] cf-mono text-[var(--cf-bg)] bg-[var(--cf-accent)] rounded-lg px-3 py-1.5">Crest Voice &mdash; responding</span></div>
                  <div className="flex justify-start"><span className="text-[11px] cf-mono text-neutral-500 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5">Caller interrupts &mdash; yielded</span></div>
                </div>
              </div>
              <div className="flex flex-wrap gap-2 mb-6">
                <span className="cf-mono text-[10px] uppercase tracking-widest text-neutral-300 border border-[var(--cf-border-15)] rounded-full px-3 py-1.5">Duplex Stream</span>
                <span className="cf-mono text-[10px] uppercase tracking-widest text-neutral-300 border border-[var(--cf-border-15)] rounded-full px-3 py-1.5">Interruption-Ready</span>
                <span className="cf-mono text-[10px] uppercase tracking-widest text-neutral-300 border border-[var(--cf-border-15)] rounded-full px-3 py-1.5">Governance On</span>
              </div>
              <div className="mt-auto">
                <span className="cf-mono text-[11px] text-[var(--cf-accent)] block mb-1">ENTERPRISE VOICE CONSOLE</span>
                <p className="cf-serif-title text-xl text-white italic">Synthetic Precision, Organic Breath</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const TRUST_LOGOS = [
  { name: "MERIDIAN TEL", cls: "cf-serif-title tracking-[0.2em]" },
  { name: "Northbeam", cls: "cf-serif-title italic" },
  { name: "CALLWRIGHT", cls: "cf-mono tracking-widest" },
  { name: "Axiom Desk", cls: "cf-serif-title" },
];

const TrustStrip = () => (
  <section data-testid="enterprise-trust-strip" className="py-14 border-b border-[var(--cf-border-10)]">
    <div className="max-w-6xl mx-auto px-6 text-center">
      <span className="cf-mono text-xs uppercase tracking-widest text-[var(--cf-accent)] block mb-8">Enterprise-Ready &mdash; Powering Customer Call Operations</span>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {TRUST_LOGOS.map((logo, i) => (
          <div
            key={logo.name}
            data-testid={`enterprise-logo-${i + 1}`}
            className="h-20 rounded-xl border border-[var(--cf-border-15)] bg-[var(--cf-card)] flex items-center justify-center"
          >
            <span className={`${logo.cls} text-lg text-neutral-400`}>{logo.name}</span>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Marquee = () => (
  <div data-testid="marquee-strip" className="py-5 border-b border-[var(--cf-border-10)] bg-[var(--cf-marquee)] overflow-hidden">
    <div className="cf-mq items-center gap-12 text-xs uppercase tracking-widest text-neutral-400">
      {[0, 1].map((track) => (
        <div key={track} className="flex items-center gap-12" aria-hidden={track === 1}>
          {MARQUEE_ITEMS.map((item) => (
            <span key={`${track}-${item}`} className="flex items-center gap-12">
              <span>{item}</span>
              <span className="text-[var(--cf-accent)]">&mdash;</span>
            </span>
          ))}
        </div>
      ))}
    </div>
  </div>
);

const Capabilities = () => (
  <section id="capabilities" data-testid="capabilities-section" className="py-24 border-b border-[var(--cf-border-10)]">
    <div className="max-w-6xl mx-auto px-6">
      <div className="text-center max-w-2xl mx-auto mb-16">
        <span className="cf-mono text-xs uppercase tracking-widest text-[var(--cf-accent)]">Core Capabilities</span>
        <h2 className="cf-serif-title text-3xl md:text-5xl text-white mt-2">Crafted For High-Stakes Operations</h2>
      </div>
      <div className="grid md:grid-cols-3 gap-8">
        {CAPABILITIES.map((cap) => (
          <div key={cap.title} data-testid={cap.testid} className="bg-[var(--cf-card)] border border-[var(--cf-border-15)] rounded-xl p-6">
            <div className="aspect-[16/10] rounded-lg overflow-hidden mb-6">
              <img src={cap.image} alt={cap.alt} className="w-full h-full object-cover cf-drift" loading="lazy" />
            </div>
            <h3 className="cf-serif-title text-xl text-white mb-2">{cap.title}</h3>
            <p className="text-sm text-neutral-400">{cap.text}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const FullBleedStrip = () => (
  <section data-testid="fullbleed-strip" className="relative py-24 border-b border-[var(--cf-border-10)] overflow-hidden">
    <img src={IMAGES.strip} alt="Panoramic auditorium with acoustic panels in amber light" className="absolute inset-0 w-full h-full object-cover opacity-25" loading="lazy" />
    <div className="absolute inset-0 bg-gradient-to-b from-[var(--cf-bg)] via-transparent to-[var(--cf-bg)]"></div>
    <div className="relative max-w-4xl mx-auto px-6 text-center">
      <span className="cf-mono text-xs uppercase tracking-widest text-[var(--cf-accent)] block mb-3">Enterprise Standard</span>
      <h2 className="cf-serif-title text-3xl md:text-5xl text-white mb-4">A Single Voice Infrastructure For Every Call Touchpoint</h2>
      <p className="text-sm text-neutral-300 max-w-xl mx-auto leading-relaxed">
        Scale inbound dispatch, appointment coordination, and customer resolution without degrading vocal standard.
      </p>
    </div>
  </section>
);

const PLATFORM_FEATURES = [
  {
    icon: "voice",
    title: "Crest Voice Agents",
    text: "Natural, interruption-aware agents that hold long customer calls without synthetic friction.",
    testid: "platform-feature-voice-agents",
  },
  {
    icon: "telephony",
    title: "Global Telephony Fabric",
    text: "SIP interconnect and carrier routing built in — plug straight into existing call operations.",
    testid: "platform-feature-telephony",
  },
  {
    icon: "governance",
    title: "Governance & Oversight",
    text: "Call review, audit trails and privacy boundaries designed for enterprise compliance teams.",
    testid: "platform-feature-governance",
  },
];

const PlatformIcons = {
  voice: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-5 h-5" aria-hidden="true">
      <path d="M12 3v3M12 18v3M8 6v12M16 6v12M4 9v6M20 9v6" strokeLinecap="round" />
    </svg>
  ),
  telephony: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-5 h-5" aria-hidden="true">
      <circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3c3 3.5 3 14.5 0 18M12 3c-3 3.5-3 14.5 0 18" />
    </svg>
  ),
  governance: (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="w-5 h-5" aria-hidden="true">
      <path d="M12 3l7 3v5c0 5-3.5 8.5-7 10-3.5-1.5-7-5-7-10V6l7-3z" /><path d="M9 12l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  ),
};

const PlatformSection = () => (
  <section id="platform" data-testid="platform-section" className="py-24 border-b border-[var(--cf-border-10)]">
    <div className="max-w-6xl mx-auto px-6">
      <div className="grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <span className="cf-mono text-xs uppercase tracking-widest text-[var(--cf-accent)] block mb-3">The Product</span>
          <h2 className="cf-serif-title text-3xl md:text-5xl text-white mb-8">One Platform. Every Customer Call.</h2>
          <div className="space-y-6">
            {PLATFORM_FEATURES.map((f) => (
              <div key={f.title} data-testid={f.testid} className="flex gap-4">
                <span className="shrink-0 w-11 h-11 rounded-full border border-[var(--cf-border-20)] text-[var(--cf-accent)] flex items-center justify-center">
                  {PlatformIcons[f.icon]}
                </span>
                <div>
                  <h3 className="cf-serif-title text-xl text-white mb-1">{f.title}</h3>
                  <p className="text-sm text-neutral-400 leading-relaxed">{f.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="lg:col-span-6">
          <div data-testid="platform-console" className="rounded-2xl border border-[var(--cf-border-20)] bg-[var(--cf-card)] p-6 md:p-7 shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <span className="cf-mono text-[11px] tracking-widest text-neutral-400 uppercase">Operations Overview</span>
              <span className="cf-mono text-[10px] tracking-widest text-emerald-300 uppercase flex items-center gap-2"><span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse"></span>All Systems Live</span>
            </div>
            <div className="space-y-3 mb-6">
              {[
                { label: "Inbound Support", state: "Routing" },
                { label: "Appointment Desk", state: "Active" },
                { label: "Outbound Renewals", state: "Queued" },
              ].map((row) => (
                <div key={row.label} className="flex items-center justify-between border border-[var(--cf-border-15)] rounded-lg px-4 py-3 bg-black/40">
                  <span className="text-sm text-neutral-200">{row.label}</span>
                  <span className="cf-mono text-[10px] uppercase tracking-widest text-[var(--cf-accent)]">{row.state}</span>
                </div>
              ))}
            </div>
            <div className="border border-[var(--cf-border-15)] rounded-lg p-4 bg-black/40">
              <span className="cf-mono text-[10px] uppercase tracking-widest text-neutral-500 block mb-3">Call Load &mdash; Today</span>
              <div className="flex items-end gap-1.5 h-16" aria-hidden="true">
                {[0.3, 0.45, 0.6, 0.5, 0.75, 0.9, 0.65, 0.8, 1, 0.7, 0.55, 0.85].map((h, i) => (
                  <span key={i} className="flex-1 rounded-sm" style={{ height: `${h * 100}%`, backgroundColor: 'rgba(226, 168, 92, 0.55)' }}></span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const TESTIMONIALS = [
  {
    quote: "Callers stopped asking if they were speaking to a machine. That changed the conversation entirely.",
    role: "VP Customer Experience",
    org: "Enterprise Telecom",
    testid: "testimonial-card-1",
  },
  {
    quote: "We routed our overflow lines through Crestfoundry and the hand-offs feel like our best agents on their best day.",
    role: "Head of Support Operations",
    org: "Global BPO",
    testid: "testimonial-card-2",
  },
  {
    quote: "The governance layer is what sold our compliance team. Every call is reviewable, every boundary explicit.",
    role: "Chief Operating Officer",
    org: "Financial Services Group",
    testid: "testimonial-card-3",
  },
];

const Testimonials = () => (
  <section data-testid="testimonials-section" className="py-24 border-b border-[var(--cf-border-10)]">
    <div className="max-w-6xl mx-auto px-6">
      <div className="text-center max-w-2xl mx-auto mb-16">
        <span className="cf-mono text-xs uppercase tracking-widest text-[var(--cf-accent)]">From The Field</span>
        <h2 className="cf-serif-title text-3xl md:text-5xl text-white mt-2">What Enterprise Teams Say</h2>
      </div>
      <div className="grid md:grid-cols-3 gap-8">
        {TESTIMONIALS.map((t) => (
          <figure key={t.testid} data-testid={t.testid} className="bg-[var(--cf-card)] border border-[var(--cf-border-15)] rounded-xl p-7 flex flex-col">
            <svg viewBox="0 0 24 24" fill="currentColor" className="w-6 h-6 text-[var(--cf-accent)] mb-4" aria-hidden="true">
              <path d="M10 7L8 11h3v6H5v-6l2-4h3zm9 0l-2 4h3v6h-6v-6l2-4h3z" />
            </svg>
            <blockquote className="text-sm text-neutral-300 leading-relaxed mb-6 flex-1">&ldquo;{t.quote}&rdquo;</blockquote>
            <figcaption>
              <span className="block text-sm text-white">{t.role}</span>
              <span className="cf-mono text-[11px] uppercase tracking-widest text-neutral-500">{t.org}</span>
            </figcaption>
          </figure>
        ))}
      </div>
    </div>
  </section>
);

const FounderContact = () => {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API}/inquiries`, form);
      setSubmitted(true);
      setForm({ name: "", email: "", message: "" });
      toast.success("You're on the waitlist — we'll reach out as access opens.");
    } catch (err) {
      toast.error("Submission failed. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="founder" data-testid="founder-section" className="py-24">
      <div className="max-w-6xl mx-auto px-6">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-5">
            <span className="cf-mono text-xs uppercase tracking-widest text-[var(--cf-accent)]">Founder Leadership</span>
            <h2 data-testid="founder-name-heading" className="cf-serif-title text-3xl md:text-4xl text-white mt-2 mb-6">Deborah Hurley</h2>
            <p data-testid="founder-note" className="text-sm text-neutral-400 leading-relaxed">
              Building Crestfoundry as enterprise-ready voice infrastructure &mdash; from first beta deployment to global call volume.
            </p>
          </div>

          <div id="contact" data-testid="contact-panel" className="lg:col-span-7 bg-[var(--cf-card)] border border-[var(--cf-border-20)] rounded-2xl p-8 md:p-10">
            <span className="cf-mono text-xs uppercase tracking-widest text-[var(--cf-accent)] block mb-2">Beta Access</span>
            <h3 className="cf-serif-title text-2xl md:text-3xl text-white mb-2">Join the Enterprise Waitlist</h3>
            <p className="text-xs text-neutral-400 mb-8">Tell us about your call volumes &mdash; we&rsquo;ll reach out as enterprise access opens.</p>

            {submitted ? (
              <div data-testid="inquiry-success-message" className="border border-[var(--cf-border-40)] rounded-xl p-8 text-center">
                <p className="cf-serif-title text-2xl text-white mb-2 italic">You&rsquo;re On The List.</p>
                <p className="text-sm text-neutral-400 mb-6">Your request is in. We&rsquo;ll reach out as beta access to the enterprise product opens.</p>
                <button
                  data-testid="inquiry-send-another-button"
                  onClick={() => setSubmitted(false)}
                  className="px-6 py-2.5 text-xs uppercase tracking-widest border border-[var(--cf-border-40)] text-[var(--cf-accent)] hover:bg-[var(--cf-accent)]/10 rounded-full transition-colors"
                >
                  Send Another
                </button>
              </div>
            ) : (
              <form data-testid="inquiry-form" onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label htmlFor="inquiry-name" className="block text-xs uppercase cf-mono text-neutral-400 mb-2">Full Name</label>
                  <input
                    data-testid="inquiry-name-input"
                    type="text" id="inquiry-name" name="name" required
                    value={form.name} onChange={handleChange}
                    className="w-full bg-black/50 border border-[var(--cf-border-20)] rounded-lg px-4 py-3 text-sm text-white focus:outline-none focus:border-[var(--cf-accent)]"
                  />
                </div>
                <div>
                  <label htmlFor="inquiry-email" className="block text-xs uppercase cf-mono text-neutral-400 mb-2">Corporate Email</label>
                  <input
                    data-testid="inquiry-email-input"
                    type="email" id="inquiry-email" name="email" required
                    value={form.email} onChange={handleChange}
                    className="w-full bg-black/50 border border-[var(--cf-border-20)] rounded-lg px-4 py-3 text-sm text-white focus:outline-none focus:border-[var(--cf-accent)]"
                  />
                </div>
                <div>
                  <label htmlFor="inquiry-message" className="block text-xs uppercase cf-mono text-neutral-400 mb-2">Inquiry Details</label>
                  <textarea
                    data-testid="inquiry-message-input"
                    id="inquiry-message" name="message" rows="3"
                    value={form.message} onChange={handleChange}
                    placeholder="Describe call volume and target workflows..."
                    className="w-full bg-black/50 border border-[var(--cf-border-20)] rounded-lg px-4 py-3 text-sm text-white focus:outline-none focus:border-[var(--cf-accent)]"
                  ></textarea>
                </div>
                <button
                  data-testid="inquiry-submit-button"
                  type="submit" disabled={submitting}
                  className="w-full py-3.5 rounded-full text-xs font-semibold uppercase tracking-widest bg-[var(--cf-accent)] text-[var(--cf-bg)] hover:bg-[var(--cf-accent-hover)] transition-colors disabled:opacity-60"
                >
                  {submitting ? "Submitting..." : "Request Beta Access"}
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

const Footer = () => (
  <footer data-testid="site-footer" className="border-t border-[var(--cf-border-10)] py-10 bg-[var(--cf-footer)]">
    <div className="max-w-6xl mx-auto px-6 flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-neutral-500">
      <span className="cf-serif-title text-base text-neutral-300">Crestfoundry</span>
      <span data-testid="footer-founder-name">Founder: <span className="text-neutral-300">Deborah Hurley</span></span>
      <span>&copy; 2025 Crestfoundry.</span>
    </div>
  </footer>
);

function App() {
  return (
    <div className="min-h-screen bg-[var(--cf-bg)] text-[var(--cf-text)] antialiased selection:bg-[var(--cf-accent)] selection:text-[var(--cf-bg)]">
      <Toaster theme="dark" position="top-center" />
      <Nav />
      <Hero />
      <TrustStrip />
      <Marquee />
      <PlatformSection />
      <Capabilities />
      <FullBleedStrip />
      <Testimonials />
      <FounderContact />
      <Footer />
    </div>
  );
}

export default App;
